import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import {
  findProjectNavigationToolByPath,
  resolveProjectNavigation,
} from "../../utils/dashboardNavigation";
import { isSuperAdmin } from "../../utils/roles";

function projectMatches(project, key) {
  if (!project || !key) return false;
  return [project.slug, project.id, project._id]
    .filter(Boolean)
    .some((value) => String(value) === String(key));
}

export default function ProjectSettingsRedirect({ section = "general" }) {
  const { projectId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { projects, activeProject, loading, loadProjects } = useProjects();
  const [lookupKey, setLookupKey] = React.useState("");
  const search = location.search || "";
  const target = `/dashboard/proyectos/${projectId}/configuracion/${section}${search}`;

  const project = React.useMemo(() => {
    const matched = projects.find((item) => projectMatches(item, projectId));
    return matched || (projectMatches(activeProject, projectId) ? activeProject : null);
  }, [projects, activeProject, projectId]);

  React.useEffect(() => {
    let cancelled = false;
    if (project) {
      setLookupKey(String(projectId || ""));
      return () => { cancelled = true; };
    }
    if (loading || lookupKey === String(projectId || "")) return () => { cancelled = true; };
    Promise.resolve(loadProjects()).finally(() => {
      if (!cancelled) setLookupKey(String(projectId || ""));
    });
    return () => { cancelled = true; };
  }, [projectId, project, loading, lookupKey, loadProjects]);

  if (!project) {
    if (loading || lookupKey !== String(projectId || "")) return null;
    return <Navigate replace to="/dashboard" />;
  }

  const navigation = resolveProjectNavigation({
    project,
    memberAccess: project.memberAccess,
    superAdmin: isSuperAdmin(user),
    pathname: target,
    search,
  });
  const targetTool = findProjectNavigationToolByPath(navigation, target, search);

  if (targetTool?.authorizationResolved && targetTool.authorized === false) {
    const firstAllowedSettings = navigation.toolsByKey.settings?.secondaryTools?.find(
      (tool) => tool.visible && tool.available && tool.authorized !== false,
    );
    const fallback = firstAllowedSettings?.path
      || navigation.tools.find((tool) => tool.visible && tool.available)?.path
      || "/dashboard";
    return <Navigate replace to={fallback} />;
  }

  return <Navigate replace to={target} />;
}
